import React, { Suspense, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import { StrokeOptions } from 'perfect-freehand'
import { RouteObject } from 'react-router-dom'

import { InspectorContext } from '@cash-web/shared-data-access-redux-inspect'
import {
  cardStudioReducer,
  DEFAULT_DEVTOOLS_SETTINGS,
  DevToolsContext,
  enabledReducer,
  InitCardStudioDevToolsArgs,
  INITIAL_CARD_STUDIO_STATE,
  ToolKey,
} from '@cash-web/shared-devtools-data-access-context'
import { EchoPluginProvider } from '@cash-web/shared-echo-feature-main'
import { getEchoClientInstance } from '@cash-web/shared-echo-feature-main'

import { getDefaultEchoServerHost, parseEchoServerAddress } from './parseEchoServerAddress'
import { Performance } from './tools/Performance'

const canvasPerformance = new Performance()

// Shuts up type error
// eslint-disable-next-line react/jsx-no-useless-fragment
const Noop = ({ children }: { children?: React.ReactNode }) => <>{children}</>

const DevToolsWrapper =
  process.env['NODE_ENV'] === 'development' || process.env['NODE_ENV'] === 'test'
    ? React.lazy(() => import('./DevToolsWrapper'))
    : Noop

type DevToolsBoundaryProps = {
  journeyRoutes?: RouteObject[] | null
  children: React.ReactNode
}
const DevToolsBoundary = ({ journeyRoutes, children }: DevToolsBoundaryProps) => {
  const [settings, dispatch] = useReducer(enabledReducer, DEFAULT_DEVTOOLS_SETTINGS)
  const echoServerKeyRef = useRef<string | null>(null)
  const [
    { imageData, showCoordinates, showGrid, context, metrics, strokeOptions, shouldCreatePreview },
    cardStudioDispatch,
  ] = useReducer(cardStudioReducer, INITIAL_CARD_STUDIO_STATE)

  const toggle = useCallback((key: ToolKey) => dispatch({ type: 'TOGGLE', key }), [dispatch])
  const setEchoServerAddress = useCallback(
    (address: string) => dispatch({ type: 'SET_ECHO_SERVER_ADDRESS', address }),
    [dispatch]
  )

  useEffect(() => {
    const echoClient = getEchoClientInstance()

    if (!settings.echoTools) {
      echoServerKeyRef.current = null
      echoClient.stop()
      return
    }

    const server = parseEchoServerAddress(settings.echoServerAddress, getDefaultEchoServerHost())
    if (!server) {
      echoServerKeyRef.current = null
      echoClient.stop()
      return
    }

    const serverKey = `${server.host}:${server.port}`
    if (echoServerKeyRef.current !== serverKey) {
      echoClient.stop()
      echoClient.setServerHost(server.host)
      echoClient.setServerPort(server.port)
      echoServerKeyRef.current = serverKey
    }

    echoClient.start()
  }, [settings.echoTools, settings.echoServerAddress])

  useEffect(() => {
    if (settings.echoTools) {
      const echoClient = getEchoClientInstance()
      echoClient.setDebugMode(settings.echoDebugMode)
    }
  }, [settings.echoTools, settings.echoDebugMode])
  const initDevTools = useCallback(({ context, metrics }: InitCardStudioDevToolsArgs) => {
    cardStudioDispatch({ type: 'SET_CONTEXT', context })
    cardStudioDispatch({ type: 'SET_METRICS', metrics })
  }, [])
  const togglePreview = useCallback(
    (value: boolean) => cardStudioDispatch({ type: 'TOGGLE_PREVIEW', value }),
    [cardStudioDispatch]
  )
  const updateDevToolsImageData = useCallback(
    (imageData: string) => {
      cardStudioDispatch({ type: 'UPDATE_IMAGE_DATA', imageData })
    },
    [cardStudioDispatch]
  )
  const toggleOption = useCallback(
    (key: 'showGrid' | 'showCoordinates') => cardStudioDispatch({ type: 'TOGGLE_CARD_STUDIO_OPTION', key }),
    [cardStudioDispatch]
  )
  const setStrokeOptions = useCallback(
    (strokeOptions: StrokeOptions) => cardStudioDispatch({ type: 'SET_STROKE_OPTIONS', strokeOptions }),
    [cardStudioDispatch]
  )
  const withPerformanceMetrics = useCallback(
    (drawFn: () => void, canvasName: string) => {
      if (settings.cardStudioTools) {
        performance.mark(`${canvasName} start`)
        canvasPerformance.begin()
      }

      drawFn()

      if (settings.cardStudioTools) {
        canvasPerformance.end()
        performance.mark(`${canvasName} end`)
        performance.measure(canvasName, `${canvasName} start`, `${canvasName} end`)
      }
    },
    [settings.cardStudioTools]
  )

  const devTools = useMemo(
    () => ({
      journeyRoutes,
      settings,
      toggle,
      cardStudio: {
        context,
        metrics,
        imageData,
        showCoordinates,
        showGrid,
        initDevTools,
        updateDevToolsImageData,
        toggleOption,
        strokeOptions,
        setStrokeOptions,
        performance: canvasPerformance,
        withPerformanceMetrics,
        togglePreview,
        shouldCreatePreview,
      },
    }),
    [
      journeyRoutes,
      settings,
      toggle,
      context,
      metrics,
      imageData,
      showCoordinates,
      showGrid,
      initDevTools,
      updateDevToolsImageData,
      toggleOption,
      strokeOptions,
      setStrokeOptions,
      withPerformanceMetrics,
      togglePreview,
      shouldCreatePreview,
    ]
  )
  const inspectorContext = useMemo(() => ({ enabled: settings.reactReduxTools }), [settings.reactReduxTools])

  return (
    <EchoPluginProvider>
      <DevToolsContext.Provider value={devTools}>
        <InspectorContext.Provider value={inspectorContext}>{children}</InspectorContext.Provider>
        <Suspense>
          <DevToolsWrapper settings={settings} toggle={toggle} setEchoServerAddress={setEchoServerAddress} />
        </Suspense>
      </DevToolsContext.Provider>
    </EchoPluginProvider>
  )
}

export const DevTools =
  process.env['NODE_ENV'] === 'development' || process.env['NODE_ENV'] === 'test' ? DevToolsBoundary : Noop
